import React, { useMemo, useState, useCallback, useEffect } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  NodeTypes,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Shield,
  FileText,
  Package,
  Key,
  AlertTriangle,
  CheckCircle,
  X,
  Maximize2,
  Network,
  Flame,
  Sparkles,
  Loader2,
  Copy,
  Check,
  GitBranch,
  Cpu,
} from 'lucide-react';
import { SecurityAnalysis, SecurityVulnerability } from '../types';
import OpenRouterService from '../services/openRouterApi';

// ── Severity palette ───────────────────────────────────────────────────────

const SEV_COLOR: Record<string, string> = {
  critical: '#ef4444',
  high:     '#f97316',
  medium:   '#eab308',
  low:      '#3b82f6',
  info:     '#6b7280',
};

const SEV_BG: Record<string, string> = {
  critical: 'rgba(239,68,68,0.08)',
  high:     'rgba(249,115,22,0.08)',
  medium:   'rgba(234,179,8,0.08)',
  low:      'rgba(59,130,246,0.08)',
  info:     'rgba(107,114,128,0.08)',
};

const OWASP_LABELS: Record<string, string> = {
  A01: 'Broken Access Control',   A02: 'Cryptographic Failures',
  A03: 'Injection',               A04: 'Insecure Design',
  A05: 'Security Misconfiguration', A06: 'Vulnerable Components',
  A07: 'Auth Failures',           A08: 'Data Integrity',
  A09: 'Logging Failures',        A10: 'SSRF',
  LLM01: 'Prompt Injection',      LLM02: 'Insecure Output',
  LLM03: 'Training Data Poison',  LLM04: 'Model DoS',
  LLM05: 'Supply Chain',          LLM06: 'Sensitive Disclosure',
  LLM07: 'Insecure Plugin',       LLM08: 'Excessive Agency',
  LLM09: 'Misinformation',        LLM10: 'LLM Jacking',
};

function owaspName(cat: string): string {
  const code = cat?.match(/^([A-Z]+\d+)/)?.[1] ?? '';
  return OWASP_LABELS[code] ?? cat?.replace(/^[A-Z0-9]+:\s*/, '') ?? cat;
}

// ── Priority scoring ───────────────────────────────────────────────────────

interface ScoredVuln extends SecurityVulnerability { _score: number; _reason: string }

function scoreVulns(security: SecurityAnalysis): ScoredVuln[] {
  const SEV_W: Record<string, number> = { critical: 40, high: 25, medium: 10, low: 3 };
  const broken: Record<string, boolean> = {
    'SOC 2': !security.compliance?.soxCompliant,
    'GDPR':  !security.compliance?.gdprCompliant,
    'HIPAA': !security.compliance?.hipaaCompliant,
    'PCI DSS': !security.compliance?.pciCompliant,
  };
  const brokenNames = Object.entries(broken).filter(([, v]) => v).map(([k]) => k);

  return (security.vulnerabilities ?? []).map(v => {
    const fileCount = v.affectedFiles?.length ?? 0;
    let score = (SEV_W[v.severity] ?? 0) + fileCount * 2 + (v.owaspCategory ? 6 : 0);

    // boost if compliance is broken and this vuln is critical/high
    if ((v.severity === 'critical' || v.severity === 'high') && brokenNames.length > 0) {
      score += brokenNames.length * 4;
    }

    const parts: string[] = [];
    if (fileCount > 0) parts.push(`${fileCount} file${fileCount !== 1 ? 's' : ''}`);
    if (v.owaspCategory)  parts.push(v.owaspCategory);
    if ((v.severity === 'critical' || v.severity === 'high') && brokenNames.length > 0) {
      parts.push(`breaks ${brokenNames.slice(0, 2).join(' & ')}`);
    }

    return { ...v, _score: score, _reason: parts.join(' · ') };
  });
}

// ── Custom ReactFlow node components ──────────────────────────────────────

const VulnNode: React.FC<{ data: SecurityVulnerability }> = ({ data }) => {
  const color = SEV_COLOR[data.severity] ?? '#6b7280';
  const bg    = SEV_BG[data.severity]    ?? 'rgba(107,114,128,0.08)';
  const title = (data.title?.length ?? 0) > 34 ? data.title.slice(0, 32) + '…' : data.title ?? 'Unknown';
  return (
    <div style={{ border: `2px solid ${color}`, background: bg, borderRadius: 8, padding: '6px 10px', minWidth: 164, maxWidth: 200, cursor: 'pointer' }}>
      <Handle type="target" position={Position.Top}   style={{ background: color, width: 8, height: 8 }} />
      <Handle type="target" position={Position.Left}  style={{ background: color, width: 8, height: 8 }} />
      <div style={{ marginBottom: 3 }}>
        <span style={{ background: color, color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {data.severity}
        </span>
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#e5e7eb', lineHeight: 1.35 }}>{title}</div>
      {data.owaspCategory && (
        <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 2 }}>{data.owaspCategory}</div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ background: color, width: 8, height: 8 }} />
      <Handle type="source" position={Position.Right}  style={{ background: color, width: 8, height: 8 }} />
    </div>
  );
};

const FileNode: React.FC<{ data: { path: string; vulnCount: number } }> = ({ data }) => {
  const parts    = data.path?.split('/') ?? ['file'];
  const filename = parts[parts.length - 1] ?? data.path;
  const dir      = parts.length > 1 ? parts[parts.length - 2] : '';
  return (
    <div style={{ border: '2px solid #4b5563', background: 'rgba(75,85,99,0.08)', borderRadius: 8, padding: '6px 10px', minWidth: 140, maxWidth: 180, cursor: 'pointer' }}>
      <Handle type="target" position={Position.Top}  style={{ background: '#6b7280', width: 8, height: 8 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <FileText style={{ width: 12, height: 12, color: '#9ca3af', flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#e5e7eb' }}>{filename}</div>
          {dir && <div style={{ fontSize: 9, color: '#6b7280' }}>{dir}/</div>}
        </div>
      </div>
      <div style={{ marginTop: 4, fontSize: 10, color: '#ef4444', fontWeight: 600 }}>
        {data.vulnCount} {data.vulnCount === 1 ? 'issue' : 'issues'}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: '#6b7280', width: 8, height: 8 }} />
      <Handle type="source" position={Position.Right}  style={{ background: '#6b7280', width: 8, height: 8 }} />
    </div>
  );
};

const OwaspNode: React.FC<{ data: { label: string; count: number } }> = ({ data }) => {
  const code = data.label?.match(/^([A-Z]+\d+)/)?.[1] ?? data.label?.slice(0, 6);
  return (
    <div style={{ border: '2px solid #8b5cf6', background: 'rgba(139,92,246,0.08)', borderRadius: 8, padding: '6px 10px', minWidth: 155, maxWidth: 195, cursor: 'pointer' }}>
      <Handle type="target" position={Position.Left}   style={{ background: '#8b5cf6', width: 8, height: 8 }} />
      <Handle type="target" position={Position.Bottom} style={{ background: '#8b5cf6', width: 8, height: 8 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <Shield style={{ width: 12, height: 12, color: '#8b5cf6', flexShrink: 0 }} />
        <span style={{ fontSize: 10, fontWeight: 700, color: '#8b5cf6' }}>{code}</span>
      </div>
      <div style={{ fontSize: 11, color: '#d1d5db', marginTop: 2, lineHeight: 1.25 }}>{owaspName(data.label)}</div>
      <div style={{ fontSize: 9, color: '#6b7280', marginTop: 2 }}>{data.count} {data.count === 1 ? 'finding' : 'findings'}</div>
      <Handle type="source" position={Position.Right} style={{ background: '#8b5cf6', width: 8, height: 8 }} />
    </div>
  );
};

const DepNode: React.FC<{ data: { label: string; cveCount: number } }> = ({ data }) => {
  const label = (data.label?.length ?? 0) > 20 ? data.label.slice(0, 18) + '…' : data.label;
  return (
    <div style={{ border: '2px solid #0d9488', background: 'rgba(13,148,136,0.08)', borderRadius: 8, padding: '6px 10px', minWidth: 130, maxWidth: 170, cursor: 'pointer' }}>
      <Handle type="target" position={Position.Top} style={{ background: '#0d9488', width: 8, height: 8 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <Package style={{ width: 12, height: 12, color: '#0d9488', flexShrink: 0 }} />
        <div style={{ fontSize: 11, fontWeight: 600, color: '#e5e7eb' }}>{label}</div>
      </div>
      <div style={{ fontSize: 10, color: '#ef4444', marginTop: 3, fontWeight: 600 }}>
        {data.cveCount} CVE{data.cveCount !== 1 ? 's' : ''}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: '#0d9488', width: 8, height: 8 }} />
    </div>
  );
};

const SecretNode: React.FC<{ data: { label: string } }> = ({ data }) => {
  const label = (data.label?.length ?? 0) > 22 ? data.label.slice(0, 20) + '…' : data.label;
  return (
    <div style={{ border: '2px solid #d97706', background: 'rgba(217,119,6,0.08)', borderRadius: 8, padding: '6px 10px', minWidth: 130, maxWidth: 180, cursor: 'pointer' }}>
      <Handle type="target" position={Position.Top} style={{ background: '#d97706', width: 8, height: 8 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <Key style={{ width: 12, height: 12, color: '#d97706', flexShrink: 0 }} />
        <div style={{ fontSize: 11, fontWeight: 600, color: '#e5e7eb' }}>{label}</div>
      </div>
      <div style={{ fontSize: 9, color: '#d97706', marginTop: 2, fontWeight: 600 }}>Secret Exposed</div>
      <Handle type="source" position={Position.Bottom} style={{ background: '#d97706', width: 8, height: 8 }} />
    </div>
  );
};

const ComplianceNode: React.FC<{ data: { label: string; score: number; compliant: boolean } }> = ({ data }) => {
  const color = data.compliant ? '#22c55e' : '#ef4444';
  return (
    <div style={{ border: `2px solid ${color}`, background: data.compliant ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)', borderRadius: 8, padding: '6px 10px', minWidth: 115, maxWidth: 155, cursor: 'pointer' }}>
      <Handle type="target" position={Position.Top}  style={{ background: color, width: 8, height: 8 }} />
      <Handle type="target" position={Position.Left} style={{ background: color, width: 8, height: 8 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        {data.compliant
          ? <CheckCircle style={{ width: 12, height: 12, color: '#22c55e', flexShrink: 0 }} />
          : <AlertTriangle style={{ width: 12, height: 12, color: '#ef4444', flexShrink: 0 }} />
        }
        <div style={{ fontSize: 12, fontWeight: 700, color }}>{data.label}</div>
      </div>
      <div style={{ fontSize: 10, color, marginTop: 2, fontWeight: 600 }}>
        {data.score}% · {data.compliant ? 'Compliant' : 'Gaps Found'}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: color, width: 8, height: 8 }} />
    </div>
  );
};

const CicdNode: React.FC<{ data: { path: string; vulnCount: number } }> = ({ data }) => {
  const parts    = data.path?.split('/') ?? ['file'];
  const filename = parts[parts.length - 1] ?? data.path;
  return (
    <div style={{ border: '2px solid #f59e0b', background: 'rgba(245,158,11,0.08)', borderRadius: 8, padding: '6px 10px', minWidth: 140, maxWidth: 180, cursor: 'pointer' }}>
      <Handle type="target" position={Position.Top}  style={{ background: '#f59e0b', width: 8, height: 8 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <GitBranch style={{ width: 12, height: 12, color: '#f59e0b', flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#e5e7eb' }}>{filename}</div>
          <div style={{ fontSize: 9, color: '#f59e0b', fontWeight: 600 }}>CI/CD Pipeline</div>
        </div>
      </div>
      <div style={{ marginTop: 4, fontSize: 10, color: '#ef4444', fontWeight: 600 }}>
        {data.vulnCount} {data.vulnCount === 1 ? 'issue' : 'issues'}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: '#f59e0b', width: 8, height: 8 }} />
      <Handle type="source" position={Position.Right}  style={{ background: '#f59e0b', width: 8, height: 8 }} />
    </div>
  );
};

const ContractNode: React.FC<{ data: { path: string; vulnCount: number } }> = ({ data }) => {
  const parts    = data.path?.split('/') ?? ['file'];
  const filename = parts[parts.length - 1] ?? data.path;
  return (
    <div style={{ border: '2px solid #a78bfa', background: 'rgba(167,139,250,0.08)', borderRadius: 8, padding: '6px 10px', minWidth: 140, maxWidth: 180, cursor: 'pointer' }}>
      <Handle type="target" position={Position.Top}  style={{ background: '#a78bfa', width: 8, height: 8 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <Cpu style={{ width: 12, height: 12, color: '#a78bfa', flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#e5e7eb' }}>{filename}</div>
          <div style={{ fontSize: 9, color: '#a78bfa', fontWeight: 600 }}>Smart Contract</div>
        </div>
      </div>
      <div style={{ marginTop: 4, fontSize: 10, color: '#ef4444', fontWeight: 600 }}>
        {data.vulnCount} {data.vulnCount === 1 ? 'issue' : 'issues'}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: '#a78bfa', width: 8, height: 8 }} />
      <Handle type="source" position={Position.Right}  style={{ background: '#a78bfa', width: 8, height: 8 }} />
    </div>
  );
};

function fileNodeType(filePath: string): string {
  if (/\.github\/workflows\/.*\.ya?ml$|(?:^|\/)Jenkinsfile$|\.gitlab-ci\.ya?ml$/i.test(filePath)) return 'cicdNode';
  if (/\.sol$/i.test(filePath)) return 'contractNode';
  return 'fileNode';
}

const NODE_TYPES: NodeTypes = {
  vulnNode:       VulnNode,
  fileNode:       FileNode,
  owaspNode:      OwaspNode,
  depNode:        DepNode,
  secretNode:     SecretNode,
  complianceNode: ComplianceNode,
  cicdNode:       CicdNode,
  contractNode:   ContractNode,
};

// ── Graph data builder ─────────────────────────────────────────────────────

function buildGraphData(security: SecurityAnalysis): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const SEV_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
  const vulns = [...(security.vulnerabilities ?? [])]
    .sort((a, b) => (SEV_RANK[a.severity] ?? 5) - (SEV_RANK[b.severity] ?? 5))
    .slice(0, 25);

  // OWASP category nodes (right column)
  const owaspMap = new Map<string, number>();
  vulns.forEach(v => {
    if (v.owaspCategory) owaspMap.set(v.owaspCategory, (owaspMap.get(v.owaspCategory) ?? 0) + 1);
  });
  const owaspList = Array.from(owaspMap.entries());
  owaspList.forEach(([cat, count], idx) => {
    nodes.push({ id: `owasp-${cat}`, type: 'owaspNode', position: { x: 1120, y: 60 + idx * 100 }, draggable: true, data: { label: cat, count } });
  });

  // Vulnerability nodes (centre grid, 3 columns)
  vulns.forEach((vuln, idx) => {
    nodes.push({
      id: `vuln-${vuln.id}`,
      type: 'vulnNode',
      position: { x: 440 + (idx % 3) * 210, y: 60 + Math.floor(idx / 3) * 120 },
      draggable: true,
      data: vuln,
    });
    if (vuln.owaspCategory && owaspMap.has(vuln.owaspCategory)) {
      edges.push({ id: `e-vo-${vuln.id}`, source: `vuln-${vuln.id}`, target: `owasp-${vuln.owaspCategory}`, type: 'smoothstep', style: { stroke: '#8b5cf6', strokeWidth: 1.2, opacity: 0.5 } });
    }
  });

  const vulnGridBottom = 60 + Math.ceil(vulns.length / 3) * 120;

  // File nodes (left column)
  const fileMap = new Map<string, string[]>();
  vulns.forEach(v => (v.affectedFiles ?? []).forEach(f => fileMap.set(f, [...(fileMap.get(f) ?? []), v.id])));
  const topFiles = Array.from(fileMap.entries()).sort((a, b) => b[1].length - a[1].length).slice(0, 14);
  topFiles.forEach(([filePath, vulnIds], idx) => {
    nodes.push({ id: `file-${filePath}`, type: fileNodeType(filePath), position: { x: 50, y: 60 + idx * 95 }, draggable: true, data: { path: filePath, vulnCount: vulnIds.length } });
    vulnIds.forEach(vid => {
      if (vulns.find(v => v.id === vid)) {
        edges.push({ id: `e-fv-${filePath.replace(/\W/g, '_')}-${vid}`, source: `file-${filePath}`, target: `vuln-${vid}`, type: 'smoothstep', style: { stroke: '#6b7280', strokeWidth: 1, opacity: 0.35 } });
      }
    });
  });

  // Dependency nodes (below vuln grid)
  const depVulns = vulns.filter(v => v.category === 'dependency');
  const depMap = new Map<string, { cveCount: number; vulnIds: string[] }>();
  depVulns.forEach(v => {
    const name = v.title?.match(/^([^@\s]+)/)?.[1] ?? 'unknown';
    const e = depMap.get(name) ?? { cveCount: 0, vulnIds: [] };
    e.cveCount++; e.vulnIds.push(v.id);
    depMap.set(name, e);
  });
  const topDeps = Array.from(depMap.entries()).slice(0, 8);
  topDeps.forEach(([depName, data], idx) => {
    nodes.push({ id: `dep-${depName}`, type: 'depNode', position: { x: 440 + idx * 185, y: vulnGridBottom + 60 }, draggable: true, data: { label: depName, cveCount: data.cveCount } });
    data.vulnIds.forEach(vid => {
      if (vulns.find(v => v.id === vid)) {
        edges.push({ id: `e-dv-${depName}-${vid}`, source: `dep-${depName}`, target: `vuln-${vid}`, type: 'smoothstep', style: { stroke: '#0d9488', strokeWidth: 1.5, opacity: 0.55 } });
      }
    });
  });

  // Secret nodes (top row)
  const secretTypes = Array.from(new Set(security.codeSecurity?.hardcodedSecrets ?? [])).slice(0, 6);
  secretTypes.forEach((secret, idx) => {
    nodes.push({ id: `secret-${idx}`, type: 'secretNode', position: { x: 50 + idx * 185, y: -110 }, draggable: true, data: { label: secret } });
  });

  // Compliance nodes (bottom row)
  const depsBottom   = topDeps.length > 0 ? vulnGridBottom + 200 : vulnGridBottom + 60;
  const complianceY  = depsBottom + 60;
  const frameworks = [
    { id: 'soc2',  label: 'SOC 2',   compliant: security.compliance?.soxCompliant  ?? false, score: security.compliance?.complianceReports?.sox?.score  ?? 0 },
    { id: 'gdpr',  label: 'GDPR',    compliant: security.compliance?.gdprCompliant  ?? false, score: security.compliance?.complianceReports?.gdpr?.score  ?? 0 },
    { id: 'hipaa', label: 'HIPAA',   compliant: security.compliance?.hipaaCompliant ?? false, score: security.compliance?.complianceReports?.hipaa?.score ?? 0 },
    { id: 'pci',   label: 'PCI DSS', compliant: security.compliance?.pciCompliant   ?? false, score: security.compliance?.complianceReports?.pci?.score   ?? 0 },
  ];
  frameworks.forEach((fw, idx) => {
    nodes.push({ id: `compliance-${fw.id}`, type: 'complianceNode', position: { x: 380 + idx * 215, y: complianceY }, draggable: true, data: fw });
    if (!fw.compliant) {
      vulns.filter(v => v.severity === 'critical' || v.severity === 'high').slice(0, 3).forEach(v => {
        edges.push({ id: `e-vc-${v.id}-${fw.id}`, source: `vuln-${v.id}`, target: `compliance-${fw.id}`, type: 'smoothstep', style: { stroke: '#ef4444', strokeWidth: 1, opacity: 0.25, strokeDasharray: '5,5' } });
      });
    }
  });

  return { nodes, edges };
}

// ── Top 3 priority callout ─────────────────────────────────────────────────

interface TopPriorityCalloutProps {
  security: SecurityAnalysis;
  onSelectVuln: (id: string) => void;
}

const TopPriorityCallout: React.FC<TopPriorityCalloutProps> = ({ security, onSelectVuln }) => {
  const top3 = useMemo(() => {
    const scored = scoreVulns(security);
    return scored.sort((a, b) => b._score - a._score).slice(0, 3);
  }, [security]);

  if (top3.length === 0) return null;

  return (
    <div style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <Flame style={{ width: 15, height: 15, color: '#ef4444' }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: '#f87171' }}>Fix these first</span>
        <span style={{ fontSize: 11, color: '#6b7280' }}>— highest blast radius across files, OWASP categories &amp; compliance</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
        {top3.map((v, i) => {
          const color = SEV_COLOR[v.severity] ?? '#6b7280';
          const title = (v.title?.length ?? 0) > 42 ? v.title.slice(0, 40) + '…' : v.title;
          return (
            <button
              key={v.id}
              onClick={() => onSelectVuln(v.id)}
              style={{ textAlign: 'left', background: '#0f1412', border: `1px solid ${color}30`, borderLeft: `3px solid ${color}`, borderRadius: 7, padding: '10px 12px', cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#1a1f1d')}
              onMouseLeave={e => (e.currentTarget.style.background = '#0f1412')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 9, fontWeight: 800, color: '#4b5563', letterSpacing: 1 }}>#{i + 1}</span>
                <span style={{ background: color, color: '#fff', borderRadius: 3, padding: '0px 5px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase' }}>
                  {v.severity}
                </span>
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#e5e7eb', lineHeight: 1.35, marginBottom: 5 }}>{title}</div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>{v._reason}</div>
              <div style={{ marginTop: 6, fontSize: 10, color: color, fontWeight: 600 }}>
                Click to inspect in graph →
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── AI fix generator (used inside DetailPanel) ─────────────────────────────

type FixState = 'idle' | 'loading' | 'done' | 'error';

// Best free models for security fix generation, in preference order.
// Nemotron Ultra (550B) wins on complex/ambiguous vulns due to deeper reasoning.
// Cohere North Mini Code is faster and equally good on well-known vuln types.
const AI_FIX_MODELS = [
  'nvidia/nemotron-3-ultra-550b-a55b:free',
  'cohere/north-mini-code:free',
  'openrouter/free',
];

async function generateAiFix(vuln: SecurityVulnerability): Promise<string> {
  const svc = new OpenRouterService();
  const prompt = `You are a senior security engineer reviewing a vulnerability found by a static analyser.

Vulnerability:
- Title: ${vuln.title}
- Severity: ${vuln.severity.toUpperCase()}
- OWASP Category: ${vuln.owaspCategory ?? 'N/A'}  (${owaspName(vuln.owaspCategory ?? '')})
- Description: ${vuln.description ?? 'N/A'}
- Affected file: ${vuln.affectedFiles?.[0] ?? 'unknown'}
${vuln.vulnerableCode ? `- Vulnerable code:\n\`\`\`\n${vuln.vulnerableCode.slice(0, 600)}\n\`\`\`` : ''}

Respond with exactly three sections — no preamble:

**Why it's dangerous**
One sentence explaining the real-world attack scenario.

**Fixed code**
Provide only the corrected code snippet (not a diff). If no code snippet is available, write the specific change needed as a code comment.

**How to verify**
One sentence on confirming the fix worked.`;

  let lastErr: Error | null = null;
  for (const model of AI_FIX_MODELS) {
    try {
      return await svc.textChat(
        model,
        [{ role: 'user', content: prompt }],
        { temperature: 0.15, max_tokens: 1500 }
      );
    } catch (err) {
      lastErr = err as Error;
      // Only fall through to next model on 4xx availability errors
      if (!(err instanceof Error) || !/4\d\d/.test(err.message)) throw err;
    }
  }
  throw lastErr ?? new Error('All AI models unavailable');
}

// ── Detail side panel ──────────────────────────────────────────────────────

interface DetailPanelProps {
  node: Node | null;
  onClose: () => void;
}

const DetailPanel: React.FC<DetailPanelProps> = ({ node, onClose }) => {
  const [fixState, setFixState]   = useState<FixState>('idle');
  const [fixContent, setFixContent] = useState('');
  const [fixError, setFixError]   = useState('');
  const [copied, setCopied]       = useState(false);

  // Reset fix state when a different node is selected
  useEffect(() => {
    setFixState('idle');
    setFixContent('');
    setFixError('');
    setCopied(false);
  }, [node?.id]);

  if (!node) return null;
  const { type, data } = node;

  const handleGenerateFix = async () => {
    setFixState('loading');
    try {
      const result = await generateAiFix(data as SecurityVulnerability);
      setFixContent(result);
      setFixState('done');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to generate fix.';
      const isNoKey = msg.toLowerCase().includes('api key');
      setFixError(isNoKey ? 'No OpenRouter API key configured. Add REACT_APP_OPENROUTER_API_KEY to enable AI fixes.' : msg);
      setFixState('error');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(fixContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const panelStyle: React.CSSProperties = {
    position: 'absolute', top: 0, right: 0, bottom: 0, width: 320,
    background: '#131816', borderLeft: '1px solid #1f2937',
    overflowY: 'auto', zIndex: 20, padding: 16,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1,
  };

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={labelStyle}>{type?.replace('Node', '') ?? 'Node'}</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 2, lineHeight: 1 }}>
          <X style={{ width: 15, height: 15 }} />
        </button>
      </div>

      {/* ── Vulnerability ── */}
      {type === 'vulnNode' && (
        <div>
          <span style={{ background: SEV_COLOR[data.severity] ?? '#6b7280', color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>
            {data.severity}
          </span>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#e5e7eb', marginTop: 8, marginBottom: 5, lineHeight: 1.4 }}>{data.title}</div>

          {data.owaspCategory && (
            <div style={{ fontSize: 11, color: '#8b5cf6', marginBottom: 8 }}>
              {data.owaspCategory} — {owaspName(data.owaspCategory)}
            </div>
          )}
          {data.description && (
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10, lineHeight: 1.55 }}>{data.description}</div>
          )}
          {(data.affectedFiles?.length ?? 0) > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ ...labelStyle, marginBottom: 4 }}>Affected Files</div>
              {data.affectedFiles.map((f: string, i: number) => (
                <div key={i} style={{ fontSize: 11, color: '#d1d5db', padding: '3px 0', borderBottom: '1px solid #1f2937', fontFamily: 'monospace', wordBreak: 'break-all' }}>{f}</div>
              ))}
            </div>
          )}
          {data.vulnerableCode && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ ...labelStyle, marginBottom: 4 }}>Vulnerable Code</div>
              <pre style={{ fontSize: 10, color: '#fca5a5', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 4, padding: 8, overflowX: 'auto', whiteSpace: 'pre-wrap', margin: 0 }}>
                {data.vulnerableCode}
              </pre>
            </div>
          )}
          {data.recommendation && (
            <div style={{ background: 'rgba(34,197,94,0.07)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 6, padding: 10, marginBottom: 10 }}>
              <div style={{ ...labelStyle, color: '#22c55e', marginBottom: 4 }}>Recommendation</div>
              <div style={{ fontSize: 12, color: '#d1d5db', lineHeight: 1.55 }}>{data.recommendation}</div>
            </div>
          )}

          {/* Metadata */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 12 }}>
            {data.cve       && <div style={{ fontSize: 11, color: '#6b7280' }}>CVE: {data.cve}</div>}
            {data.cvss      && <div style={{ fontSize: 11, color: '#6b7280' }}>CVSS: {data.cvss}</div>}
            {data.lineNumber && <div style={{ fontSize: 11, color: '#6b7280' }}>Line: {data.lineNumber}</div>}
            {data.cwe?.length > 0 && <div style={{ fontSize: 11, color: '#6b7280' }}>CWE: {data.cwe.join(', ')}</div>}
          </div>
          {data.verdict && (
            <div style={{ marginBottom: 12, padding: '4px 8px', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 4, fontSize: 11, color: '#8b5cf6' }}>
              AI Verdict: <strong>{data.verdict}</strong>
              {data.verdictConfidence != null && ` (${Math.round(data.verdictConfidence * 100)}% confidence)`}
            </div>
          )}

          {/* ── AI Fix Generator ── */}
          <div style={{ borderTop: '1px solid #1f2937', paddingTop: 12 }}>
            <div style={{ ...labelStyle, marginBottom: 8 }}>AI Fix Generator</div>

            {fixState === 'idle' && (
              <button
                onClick={handleGenerateFix}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.35)', color: '#a78bfa', borderRadius: 7, padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, width: '100%', justifyContent: 'center' }}
              >
                <Sparkles style={{ width: 14, height: 14 }} />
                Generate Fix with AI
              </button>
            )}

            {fixState === 'loading' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#8b5cf6', fontSize: 12 }}>
                <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                <span>Analysing with AI…</span>
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              </div>
            )}

            {fixState === 'error' && (
              <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 6, padding: 10 }}>
                <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 6 }}>{fixError}</div>
                <button onClick={handleGenerateFix} style={{ fontSize: 11, color: '#9ca3af', background: 'none', border: '1px solid #374151', borderRadius: 4, padding: '3px 8px', cursor: 'pointer' }}>
                  Retry
                </button>
              </div>
            )}

            {fixState === 'done' && (
              <div>
                <div style={{ background: '#0c0e0d', border: '1px solid #1f2937', borderRadius: 7, padding: 12, fontSize: 12, color: '#d1d5db', lineHeight: 1.65, whiteSpace: 'pre-wrap', marginBottom: 8 }}>
                  {fixContent}
                </div>
                <button
                  onClick={handleCopy}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, background: copied ? 'rgba(34,197,94,0.1)' : 'rgba(75,85,99,0.15)', border: `1px solid ${copied ? 'rgba(34,197,94,0.3)' : '#374151'}`, color: copied ? '#22c55e' : '#9ca3af', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                >
                  {copied ? <Check style={{ width: 12, height: 12 }} /> : <Copy style={{ width: 12, height: 12 }} />}
                  {copied ? 'Copied!' : 'Copy Fix'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── File ── */}
      {type === 'fileNode' && (
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#d1d5db', marginBottom: 10, wordBreak: 'break-all' }}>{data.path}</div>
          <span style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
            {data.vulnCount} {data.vulnCount === 1 ? 'vulnerability' : 'vulnerabilities'}
          </span>
          <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280', lineHeight: 1.55 }}>
            This file contains security issues. Click the connected vulnerability nodes for full details on each finding.
          </div>
        </div>
      )}

      {/* ── OWASP ── */}
      {type === 'owaspNode' && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#8b5cf6', marginBottom: 4 }}>{data.label}</div>
          <div style={{ fontSize: 13, color: '#d1d5db', marginBottom: 8 }}>{owaspName(data.label)}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {data.count} {data.count === 1 ? 'finding' : 'findings'} in this category across the repository.
          </div>
        </div>
      )}

      {/* ── Dependency ── */}
      {type === 'depNode' && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0d9488', marginBottom: 8 }}>{data.label}</div>
          <span style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
            {data.cveCount} CVE{data.cveCount !== 1 ? 's' : ''}
          </span>
          <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280', lineHeight: 1.55 }}>
            This dependency introduces known vulnerabilities. Review the connected vuln nodes for CVE details, then update or replace the package.
          </div>
        </div>
      )}

      {/* ── Secret ── */}
      {type === 'secretNode' && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#d97706', marginBottom: 8 }}>{data.label}</div>
          <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.55, marginBottom: 10 }}>
            A secret or API key matching this pattern was detected in the codebase. Exposed credentials can enable unauthorised access, data breaches, and account takeovers.
          </div>
          <div style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, padding: 10, fontSize: 12, color: '#d1d5db', lineHeight: 1.55 }}>
            Rotate this credential immediately, remove it from the codebase, and store it in an environment variable or a secrets manager.
          </div>
        </div>
      )}

      {/* ── Compliance ── */}
      {type === 'complianceNode' && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: data.compliant ? '#22c55e' : '#ef4444', marginBottom: 8 }}>{data.label}</div>
          <span style={{ background: data.compliant ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)', color: data.compliant ? '#22c55e' : '#ef4444', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
            {data.score}% · {data.compliant ? 'Compliant' : 'Gaps Found'}
          </span>
          <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280', lineHeight: 1.55 }}>
            {data.compliant
              ? "This framework's requirements are currently met based on the code analysis."
              : 'Security findings in this repository are blocking compliance. Remediate the connected critical/high vulnerabilities to improve this score.'}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Filter helpers ─────────────────────────────────────────────────────────

interface SevFilter  { critical: boolean; high: boolean; medium: boolean; low: boolean }
interface TypeFilter { vuln: boolean; file: boolean; owasp: boolean; dep: boolean; secret: boolean; compliance: boolean; cicd: boolean; contract: boolean }

function applyFilters(n: Node, sf: SevFilter, tf: TypeFilter): boolean {
  if (n.type === 'vulnNode')       return tf.vuln && (sf[n.data.severity as keyof SevFilter] ?? true);
  if (n.type === 'fileNode')       return tf.file;
  if (n.type === 'owaspNode')      return tf.owasp;
  if (n.type === 'depNode')        return tf.dep;
  if (n.type === 'secretNode')     return tf.secret;
  if (n.type === 'complianceNode') return tf.compliance;
  if (n.type === 'cicdNode')       return tf.cicd;
  if (n.type === 'contractNode')   return tf.contract;
  return true;
}

// ── Main component ─────────────────────────────────────────────────────────

interface SecurityKnowledgeGraphProps {
  security: SecurityAnalysis;
  repoName?: string;
}

const SecurityKnowledgeGraph: React.FC<SecurityKnowledgeGraphProps> = ({ security, repoName }) => {
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [isMaximized, setIsMaximized]   = useState(false);
  const [sevFilter, setSevFilter]       = useState<SevFilter>({ critical: true, high: true, medium: true, low: true });
  const [typeFilter, setTypeFilter]     = useState<TypeFilter>({ vuln: true, file: true, owasp: true, dep: true, secret: true, compliance: true, cicd: true, contract: true });

  const { nodes: rawNodes, edges: rawEdges } = useMemo(() => buildGraphData(security), [security]);
  const [nodes, setNodes, onNodesChange] = useNodesState(rawNodes);
  const [edges, setEdges]                = useEdgesState(rawEdges);

  useEffect(() => { setNodes(rawNodes); }, [rawNodes, setNodes]);
  useEffect(() => { setEdges(rawEdges); }, [rawEdges, setEdges]);

  const displayNodes = useMemo(
    () => nodes.map(n => ({ ...n, hidden: !applyFilters(n, sevFilter, typeFilter) })),
    [nodes, sevFilter, typeFilter]
  );
  const displayEdges = useMemo(() => {
    const visIds = new Set(displayNodes.filter(n => !n.hidden).map(n => n.id));
    return edges.filter(e => visIds.has(e.source as string) && visIds.has(e.target as string));
  }, [edges, displayNodes]);

  const onNodeClick = useCallback((_: React.MouseEvent, n: Node) => {
    setSelectedNode(prev => prev?.id === n.id ? null : n);
  }, []);

  // Escape closes fullscreen
  useEffect(() => {
    if (!isMaximized) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsMaximized(false); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [isMaximized]);

  // When user clicks a priority card — find & select the vuln node
  const handleSelectVuln = useCallback((vulnId: string) => {
    const n = displayNodes.find(nd => nd.id === `vuln-${vulnId}` && !nd.hidden);
    if (n) setSelectedNode(n);
  }, [displayNodes]);

  const stats = useMemo(() => {
    const vulns     = security.vulnerabilities ?? [];
    const files     = new Set(vulns.flatMap(v => v.affectedFiles ?? []));
    const owaspCats = new Set(vulns.map(v => v.owaspCategory).filter(Boolean));
    const brokenFws = [
      !security.compliance?.gdprCompliant  && 'GDPR',
      !security.compliance?.soxCompliant   && 'SOC 2',
      !security.compliance?.hipaaCompliant && 'HIPAA',
      !security.compliance?.pciCompliant   && 'PCI DSS',
    ].filter(Boolean) as string[];
    return { vulns: vulns.length, files: files.size, owaspCats: owaspCats.size, brokenFws, secretCount: security.codeSecurity?.hardcodedSecrets?.length ?? 0 };
  }, [security]);

  const graphCanvas = (fullScreen: boolean) => (
    <div style={{ position: 'relative', flex: 1, display: 'flex', minHeight: fullScreen ? '100%' : 540 }}>
      {rawNodes.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0c0e0d', flexDirection: 'column', gap: 12 }}>
          <CheckCircle style={{ width: 40, height: 40, color: '#22c55e' }} />
          <div style={{ fontSize: 16, fontWeight: 700, color: '#e5e7eb' }}>No attack surface detected</div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>No security findings to map for this repository.</div>
        </div>
      ) : (
        <>
          <ReactFlow
            nodes={displayNodes}
            edges={displayEdges}
            nodeTypes={NODE_TYPES}
            onNodesChange={onNodesChange}
            onNodeClick={onNodeClick}
            onPaneClick={() => setSelectedNode(null)}
            nodesDraggable
            nodesConnectable={false}
            fitView
            fitViewOptions={{ padding: 0.18 }}
            minZoom={0.08}
            maxZoom={2.5}
            proOptions={{ hideAttribution: true }}
            style={{ background: '#0c0e0d', flex: 1 }}
          >
            <Controls />
            <Background color="#1a1f1d" gap={20} />
            <MiniMap
              nodeColor={n => {
                if (n.type === 'vulnNode')       return SEV_COLOR[n.data.severity] ?? '#6b7280';
                if (n.type === 'owaspNode')       return '#8b5cf6';
                if (n.type === 'fileNode')        return '#4b5563';
                if (n.type === 'depNode')         return '#0d9488';
                if (n.type === 'secretNode')      return '#d97706';
                if (n.type === 'complianceNode')  return n.data.compliant ? '#22c55e' : '#ef4444';
                return '#6b7280';
              }}
              style={{ background: '#0c0e0d', border: '1px solid #1f2937' }}
            />
          </ReactFlow>
          <DetailPanel node={selectedNode} onClose={() => setSelectedNode(null)} />
        </>
      )}
    </div>
  );

  const controlBar = (fullScreen: boolean) => (
    <div style={{ background: '#111816', borderBottom: '1px solid #1f2937', padding: '10px 16px' }}>
      {/* Title row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Network style={{ width: 17, height: 17, color: '#8b5cf6' }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: '#e5e7eb' }}>Security Knowledge Graph</span>
          <span style={{ fontSize: 10, background: 'rgba(139,92,246,0.14)', color: '#8b5cf6', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 99, padding: '2px 8px', fontWeight: 600 }}>
            Attack Surface Map
          </span>
          {repoName && fullScreen && <span style={{ fontSize: 12, color: '#6b7280' }}>— {repoName}</span>}
        </div>
        {!fullScreen ? (
          <button onClick={() => setIsMaximized(true)} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)', color: '#8b5cf6', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
            <Maximize2 style={{ width: 11, height: 11 }} /> Full Screen
          </button>
        ) : (
          <button onClick={() => setIsMaximized(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, lineHeight: 1 }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        )}
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 18px', marginBottom: 9 }}>
        {[
          { label: 'Vulnerabilities',  value: stats.vulns,            color: '#ef4444' },
          { label: 'Files Affected',   value: stats.files,            color: '#9ca3af' },
          { label: 'OWASP Categories', value: stats.owaspCats,        color: '#8b5cf6' },
          { label: 'Secrets Exposed',  value: stats.secretCount,      color: '#d97706' },
          { label: 'Compliance Gaps',  value: stats.brokenFws.length, color: stats.brokenFws.length > 0 ? '#ef4444' : '#22c55e' },
        ].map(s => (
          <span key={s.label} style={{ fontSize: 12, color: '#6b7280' }}>
            <strong style={{ color: s.color, marginRight: 3 }}>{s.value}</strong>{s.label}
          </span>
        ))}
        {stats.brokenFws.length > 0 && (
          <span style={{ fontSize: 11, color: '#ef4444' }}>({stats.brokenFws.join(', ')} at risk)</span>
        )}
      </div>

      {/* Filter row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '3px 10px' }}>
        <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Severity:</span>
        {(['critical', 'high', 'medium', 'low'] as const).map(sev => (
          <label key={sev} style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 11 }}>
            <input type="checkbox" checked={sevFilter[sev]} onChange={e => setSevFilter(p => ({ ...p, [sev]: e.target.checked }))} style={{ accentColor: SEV_COLOR[sev], width: 12, height: 12 }} />
            <span style={{ color: sevFilter[sev] ? SEV_COLOR[sev] : '#4b5563', fontWeight: 600, textTransform: 'capitalize' }}>{sev}</span>
          </label>
        ))}
        <span style={{ color: '#374151', fontSize: 11 }}>|</span>
        <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Show:</span>
        {([
          { key: 'vuln'       as const, label: 'Vulns',      color: '#ef4444' },
          { key: 'file'       as const, label: 'Files',      color: '#6b7280' },
          { key: 'owasp'      as const, label: 'OWASP',      color: '#8b5cf6' },
          { key: 'dep'        as const, label: 'Deps',       color: '#0d9488' },
          { key: 'secret'     as const, label: 'Secrets',    color: '#d97706' },
          { key: 'compliance' as const, label: 'Compliance', color: '#22c55e' },
          { key: 'cicd'       as const, label: 'CI/CD',      color: '#f59e0b' },
          { key: 'contract'   as const, label: 'Contracts',  color: '#a78bfa' },
        ]).map(({ key, label, color }) => (
          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 11 }}>
            <input type="checkbox" checked={typeFilter[key]} onChange={e => setTypeFilter(p => ({ ...p, [key]: e.target.checked }))} style={{ accentColor: color, width: 12, height: 12 }} />
            <span style={{ color: typeFilter[key] ? color : '#4b5563', fontWeight: 600 }}>{label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const legend = (
    <div style={{ background: '#0f1412', borderBottom: '1px solid #1a1f1d', padding: '4px 16px', display: 'flex', flexWrap: 'wrap', gap: '2px 12px' }}>
      {[
        { color: '#ef4444', label: 'Critical vuln' },
        { color: '#f97316', label: 'High vuln' },
        { color: '#eab308', label: 'Medium vuln' },
        { color: '#3b82f6', label: 'Low vuln' },
        { color: '#4b5563', label: 'File' },
        { color: '#8b5cf6', label: 'OWASP category' },
        { color: '#0d9488', label: 'Vulnerable dep' },
        { color: '#d97706', label: 'Secret' },
        { color: '#ef4444', label: 'Breaks compliance', dashed: true },
      ].map(({ color, label, dashed }) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: dashed ? 14 : 8, height: dashed ? 0 : 8, borderRadius: dashed ? 0 : 2, background: dashed ? undefined : color, flexShrink: 0, borderTop: dashed ? `2px dashed ${color}` : undefined }} />
          <span style={{ fontSize: 9, color: '#6b7280' }}>{label}</span>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {/* Inline card */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        {controlBar(false)}
        {legend}
        {/* Priority callout */}
        <div style={{ background: '#0c0e0d', padding: '12px 16px 0' }}>
          <TopPriorityCallout security={security} onSelectVuln={handleSelectVuln} />
        </div>
        <div style={{ height: 540, display: 'flex' }}>
          {graphCanvas(false)}
        </div>
        <div style={{ background: '#0f1412', borderTop: '1px solid #1a1f1d', padding: '4px 16px' }}>
          <span style={{ fontSize: 10, color: '#374151' }}>
            Drag nodes to rearrange · Click any node to inspect · ✨ Generate AI Fix on vulnerability nodes · Scroll to zoom
          </span>
        </div>
      </div>

      {/* Full-screen modal */}
      {isMaximized && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <div style={{ background: '#0c0e0d', borderRadius: 12, width: '100%', height: '100%', maxWidth: '96vw', maxHeight: '96vh', display: 'flex', flexDirection: 'column', border: '1px solid #1f2937', overflow: 'hidden' }}>
            {controlBar(true)}
            {legend}
            <div style={{ background: '#0c0e0d', padding: '10px 16px 0' }}>
              <TopPriorityCallout security={security} onSelectVuln={handleSelectVuln} />
            </div>
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
              {graphCanvas(true)}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SecurityKnowledgeGraph;
